import {fetcher, RequestMethod} from '#utils/http.js';
import type {UseQueryOptions} from '@tanstack/react-query';
import type {Nullable} from '#types/general.js';
import {useQuery} from '@tanstack/react-query';
import type {DatasetGroupQueueEventDataset} from '#types/dataset_groups.js';

type QueueEventFieldSearchParams = {
  id: number;
  limit?: number;
  page?: number;
  query?: string;
};

type QueueEventFieldSearchResultItem = {
  display_name: string;
  id: string;
  name: string;
};

type QueueEventFieldSearchResponse = {
  more: boolean;
  page: number;
  results: QueueEventFieldSearchResultItem[];
};

const PAGE_LIMIT = 20;

export async function searchQueueEventFields({
  id,
  limit = PAGE_LIMIT,
  page = 1,
  query = '',
}: QueueEventFieldSearchParams): Promise<QueueEventFieldSearchResponse> {
  const searchParams = new URLSearchParams();

  searchParams.set('limit', String(limit));
  searchParams.set('page', String(page));

  if (query) {
    searchParams.set('query', query);
  }

  const url = `/api/v2/queue_events/${id}/field_search?${searchParams.toString()}`;

  return fetcher<QueueEventFieldSearchResponse>(url, {
    method: RequestMethod.GET,
  });
}

export const useQueueEventSearchFields = (
  {id, limit = PAGE_LIMIT, page = 1, query = ''}: QueueEventFieldSearchParams,
  options?: Partial<UseQueryOptions<QueueEventFieldSearchResponse>>
) => {
  return useQuery<QueueEventFieldSearchResponse>({
    queryFn: () =>
      searchQueueEventFields({
        id,
        limit,
        page,
        query,
      }),
    queryKey: ['queue-event-field-search', id, limit, page, query],
    staleTime: 60 * 1000,
    ...options,
  });
};

export type RealtimeEventsResponse = {
  results: DatasetGroupQueueEventDataset[];
};

export const useRealtimeEvents = (
  datasetGroupId: Nullable<string>,
  options?: Partial<UseQueryOptions<RealtimeEventsResponse>>
) => {
  return useQuery<RealtimeEventsResponse>({
    queryFn: () =>
      datasetGroupId
        ? fetcher<RealtimeEventsResponse>(
            `/api/v2/dataset_groups/${datasetGroupId}/queue_event_datasets`,
            {
              method: RequestMethod.GET,
            }
          )
        : {results: []},
    queryKey: ['realtimeEvents', datasetGroupId],
    staleTime: 60 * 1000,
    ...options,
  });
};
